import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import { upgradeFreePlanModalOpen } from '@/components/Modals/UpgradeFreePlanModal';
import { toast } from '@/components/toast';
import { getBackRoute, useNavigateToAdvancedEditor } from '@/hooks/navigation';
import { useAllVoices } from '@/hooks/useAllVoices';
import { useFreeVideos } from '@/hooks/useFreeVideos';
import usePageTitle from '@/hooks/usePageTitle';
import { useTiming } from '@/hooks/useTiming';
import { Timeline } from '@/pages/AdvancedEditor/components/frames';
import { GenerateButton } from '@/pages/AdvancedEditor/components/header/Generate';
import { useFiltersReset } from '@/pages/AdvancedEditor/components/panels/script/utils';
import { Sidebar } from '@/pages/AdvancedEditor/components/sidebar/Sidebar';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import {
    PolotnoStoreActivePageKeys,
    setPageProperty,
    useCurrentProjectId,
    usePolotnoStoreInit,
    useSetPolotnoActivePageState,
} from '@/pages/AdvancedEditor/hooks';
import { useCleanDraft } from '@/pages/AdvancedEditor/hooks/useCleanDraft';
import { useCrop } from '@/pages/AdvancedEditor/hooks/useCrop/useCrop';
import { useSyncCurrentAvatar } from '@/pages/AdvancedEditor/hooks/useSyncCurrentAvatar';
import { useSyncNewPageInitials } from '@/pages/AdvancedEditor/hooks/useSyncNewPageInitials';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import { useTtsCacheManagement } from '@/services/tts/useTtsCacheManagement';
import { languageToVoicesMap as languageToVoicesMapState, ttsVoiceModal } from '@/state/presenters';
import theme from '@/theme';
import { AppRoutes, MixPanelEvent } from '@/utils/types';
import { isMobile } from '@/utils/utils';
import '@blueprintjs/core/lib/css/blueprint.css';
import styled from '@emotion/styled';
import CircularProgress from '@mui/material/CircularProgress';
import { observer } from 'mobx-react-lite';
import { PolotnoContainer, WorkspaceWrap } from 'polotno';
import { Workspace } from 'polotno/canvas/workspace';
import { unstable_setAnimationsEnabled } from 'polotno/config';
import { StoreType } from 'polotno/model/store';
import { DEFAULT_SECTIONS } from 'polotno/side-panel';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import '../../cssreset.css';
import { ModerationModal } from '../editor/layout/ModerationModal';
import AdvancedEditorStyles from './AdvancedEditorStyles';
import { CanvasHeader } from './components/header/CanvasHeader';
import { useMediaLimit } from './components/panels/media/useMediaLimit';
import { clearAudioCache } from './hooks/scenesPreview/scenes-preview';
import useGetProject from './hooks/useGetProject';
import { useGetWorkspaceComponents } from './hooks/useGetWorkspaceComponents';
import { useInheritSceneAvatar } from './hooks/useInheritSceneAvatar';
import { newProjectId } from './types';
import { onKeyDownHandler } from './utils';

unstable_setAnimationsEnabled(true);

const AdvancedEditor = styled.main`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    height: 100%;

    * {
        font-family: Manrope;
    }
`;

const EmptyBackground = styled.div`
    background-color: ${theme.colors.black[200]};
    width: 100%;
    height: 100%;
`;

const LoadingPage = styled.div`
    background-color: ${theme.colors.black[200]};
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const EditorWrapper = styled.div`
    display: flex;
    flex-direction: row;
    height: 100%;
`;

const StyledWorkspaceWrap = styled(WorkspaceWrap)`
    padding-top: 70px;
    background-color: ${theme.colors.black[200]};

    .konvajs-content {
        top: 0;
    }
`;

export type Page = StoreType['pages'][number];
export type Section = (typeof DEFAULT_SECTIONS)[number];

const TITLE = 'Create Video - D-ID';

export default observer(function () {
    const mixpanel = useAnalytics();
    const { withTiming, trace } = useTiming();
    useTtsCacheManagement();
    useSyncCurrentAvatar();
    useSyncNewPageInitials();

    const { store, debouncedSaveArtifacts, editorInitialized, setEditorInitialized, isGenerating } = useAdvancedEditorContext();
    const { isAnonymous } = useAuth();
    const navigate = useNavigate();

    const initializing = useRef(false);
    const setSelectedPresenter = useSetPolotnoActivePageState(PolotnoStoreActivePageKeys.selectedPresenter);

    const projectId = useCurrentProjectId();
    const { initStore: simplifiedInitStore } = usePolotnoStoreInit();

    const { cleanEmptyDraft } = useCleanDraft(store);
    const { reset } = useFiltersReset();

    const [storeWaited, setStoreWaited] = useState(false);
    const { navigateToAdvancedEditor } = useNavigateToAdvancedEditor();

    const { pathname, state: locationState } = useLocation();
    const { isFetched: voicesFetched } = useAllVoices();
    const { data: project } = useGetProject(projectId);

    const languageToVoicesMap = useRecoilValue(languageToVoicesMapState);

    const setVoiceModalState = useSetRecoilState(ttsVoiceModal);

    const workspaceComponents = useGetWorkspaceComponents();

    const setUpgradeFreePlanModalOpen = useSetRecoilState(upgradeFreePlanModalOpen);
    const { outOfFreeVideos } = useFreeVideos();
    const isFocusedOnTimeline = useRef<boolean>(false);

    // used to get indication of last element for clipboard shortcuts
    useEffect(() => {
        const eventListener = (event: MouseEvent) => {
            isFocusedOnTimeline.current = !!(event.target as HTMLElement)?.closest('.polotno-pages-timeline');
        };

        document.addEventListener('click', eventListener);

        return () => {
            document.removeEventListener('click', eventListener);
        };
    }, []);

    usePageTitle(TITLE);

    // Should be before any store changes, so we can listen to them
    useEffect(() => {
        if (storeWaited) {
            const clearChange = store.on('change', () => {
                if (!isGenerating.current) {
                    debouncedSaveArtifacts(store);
                }
            });

            return () => {
                clearChange?.();
            };
        }
    }, [storeWaited, debouncedSaveArtifacts]);

    // this handles canva & avatar crop
    useCrop();

    useEffect(() => {
        return () => {
            if (editorInitialized && storeWaited) {
                void cleanEmptyDraft();
            }
        };
    }, [editorInitialized, storeWaited]);

    useEffect(() => {
        if (isMobile()) {
            return navigate(AppRoutes.Unsupported);
        }
    }, []);

    // cleaning useEffect
    useEffect(
        () => () => {
            reset();
            setVoiceModalState(false);
            setSelectedPresenter('');
            store.pages.forEach(clearAudioCache);
            store.clear();
            store.set({ custom: {}, temporary: {} });
        },
        []
    );
    useMediaLimit();

    const initStore = useCallback(async () => {
        try {
            await withTiming('AdvancedEditor.initStore', () => simplifiedInitStore(() => setStoreWaited(true)));
            return;
        } catch (e) {
            logger.error('Error while initializing editor', { e });
            setEditorInitialized(false);
            throw e;
        }
    }, [setEditorInitialized, simplifiedInitStore, withTiming]);

    useInheritSceneAvatar();

    useEffect(() => {
        const loadedProjectSameAsUrl = project?.id === projectId;
        const voicesLoaded = !!Object.keys(languageToVoicesMap).length && voicesFetched;

        if (outOfFreeVideos) {
            setUpgradeFreePlanModalOpen(true);
            navigate(AppRoutes.VideoStudio);
        } else if (!projectId || !projectId.startsWith('prj_')) {
            const projectIdPath = pathname.split('/').pop() ?? '';
            if (projectIdPath === newProjectId && !isAnonymous) {
                // Renavigate to create a valid project id and load the draft
                void navigateToAdvancedEditor();
            } else {
                navigate(AppRoutes.Home);
            }
        } else if (!editorInitialized && loadedProjectSameAsUrl && voicesLoaded && !initializing.current) {
            initializing.current = true;
            initStore()
                .catch(error => {
                    logger.error('Problem with project initialization', { projectId, error });
                    toast.error({ title: 'Error loading project', message: 'Please try again or contact support if the issue persists.' });
                    navigate(AppRoutes.Home);
                })
                .then(() => {
                    store.history.clear();
                    if (locationState?.isEditAsNew) {
                        store.pages.forEach(page => setPageProperty(page, PolotnoStoreActivePageKeys.ttsCreated)(false));
                    }

                    mixpanel?.track('editor-initialized', {
                        event: MixPanelEvent.VIEW,
                        projectId,
                    });

                    trace('AdvancedEditor', { message: 'editor initialized' });

                    setEditorInitialized(true);
                })
                .finally(() => {
                    initializing.current = false;
                });
        }
    }, [languageToVoicesMap, projectId, project, editorInitialized, voicesFetched, trace]);

    return (
        <>
            <AdvancedEditor>
                <EditorWrapper>
                    <Sidebar backRoute={getBackRoute(locationState?.from)} />

                    {editorInitialized ? (
                        <PolotnoContainer style={{ width: '100%', height: '100%', position: 'relative' }}>
                            <CanvasHeader />
                            <GenerateButton />

                            <StyledWorkspaceWrap>
                                <ErrorBoundary
                                    fallback={<Navigate to={AppRoutes.Home} />}
                                    beforeCapture={scope => scope.setTags({ component: 'Workspace' })}>
                                    <Workspace
                                        store={store}
                                        pageControlsEnabled={false}
                                        backgroundColor={theme.colors.black[200]}
                                        pageBorderColor="transparent"
                                        components={{
                                            NoPages: () => <EmptyBackground />,
                                            ContextMenu: () => null,
                                            ...workspaceComponents,
                                        }}
                                        renderOnlyActivePage
                                        onKeyDown={(e: KeyboardEvent) => onKeyDownHandler(e, store, isFocusedOnTimeline.current)}
                                    />

                                    <Timeline />
                                </ErrorBoundary>
                            </StyledWorkspaceWrap>
                        </PolotnoContainer>
                    ) : (
                        <LoadingPage>
                            <CircularProgress size={96} />
                        </LoadingPage>
                    )}
                </EditorWrapper>
            </AdvancedEditor>

            <AdvancedEditorStyles />
            <ModerationModal />
        </>
    );
});
